<template>
  <div class="grid">
    <!-- Show Dropdown -->
    <div v-if="hasShowDropdown" class="flex flex-column gap-1 col-12 md:col-6">
      <label for="show_select">Show</label>
      <Dropdown
        id="show_select"
        :modelValue="campaign.show_id"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, show_id: $event })
        "
        :options="shows"
        optionLabel="show_title"
        optionValue="show_id"
        placeholder="Podcast Show"
        checkmark
        filter
        :class="{ 'p-invalid': submitted && !campaign.show_id }"
        :panelStyle="{ maxWidth: 'min(500px, 45vw)' }"
      />
      <small v-if="submitted && !campaign.show_id" class="p-error">
        Show is required
      </small>
    </div>

    <div class="flex flex-column gap-1 col-12 md:col-3">
      <label for="start_date">Start Date</label>
      <Calendar
        id="start_date"
        :modelValue="campaign.start_date"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, start_date: $event })
        "
        selectionMode="single"
        :manualInput="false"
      />
    </div>

    <div class="flex flex-column gap-1 col-12 md:col-3">
      <label for="end_date">End Date</label>
      <Calendar
        id="end_date"
        :modelValue="campaign.end_date"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, end_date: $event })
        "
        selectionMode="single"
        :manualInput="false"
      />
    </div>

    <div class="flex flex-column gap-1 col-12 md:col-6">
      <label for="geo_wl">Geo Whitelist</label>
      <MultiSelect
        id="geo_wl"
        :modelValue="campaign.geos_wl"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, geos_wl: $event })
        "
        :options="countries"
        optionValue="geo"
        optionLabel="name"
        placeholder="Select Countries"
        filter
        class="w-full"
      />
    </div>

    <div class="flex flex-column gap-1 col-12 md:col-6">
      <label for="geo_bl">Geo Blacklist</label>
      <MultiSelect
        id="geo_bl"
        :modelValue="campaign.geo_bl"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, geo_bl: $event })
        "
        :options="countries"
        optionValue="geo"
        optionLabel="name"
        placeholder="Select Countries"
        filter
        class="w-full"
      />
    </div>

    <div class="flex flex-column gap-1 col-12 md:col-6">
      <label for="sellers_wl">Seller Whitelist</label>
      <MultiSelect
        id="sellers_wl"
        :modelValue="campaign.sellers_wl"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, sellers_wl: $event })
        "
        :options="sellers"
        optionValue="value"
        :optionLabel="(option) => `[${option.id}] ${option.name}`"
        placeholder="Select Seller"
        filter
        class="w-full"
      />
    </div>

    <div class="flex flex-column gap-1 col-12 md:col-6">
      <label for="sellers_bl">Seller Blacklist</label>
      <MultiSelect
        id="sellers_bl"
        :modelValue="campaign.sellers_bl"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, sellers_bl: $event })
        "
        :options="sellers"
        optionValue="value"
        :optionLabel="(option) => `[${option.id}] ${option.name}`"
        placeholder="Select Seller"
        filter
        class="w-full"
      />
    </div>

    <div class="col-12 md:col-3 flex flex-column gap-1">
      <label for="campaign_type">Campaign Type</label>
      <Dropdown
        id="campaign_type"
        :modelValue="campaign.campaign_type"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, campaign_type: $event })
        "
        :options="campaignTypeOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Choose Campaign Type"
      />
      <div v-if="campaign.campaign_type === 'publisher'">
        <label for="publisher_id">Publisher ID</label>
        <Dropdown
          id="publisher_id"
          :modelValue="campaign.publisher_id"
          @update:modelValue="
            $emit('update:campaign', { ...campaign, publisher_id: $event })
          "
          :options="sellers"
          optionValue="value"
          :optionLabel="(option) => `[${option.id}] ${option.name}`"
          placeholder="Select Publisher"
          :class="{
            'p-invalid':
              submitted &&
              campaign.campaign_type === 'publisher' &&
              !campaign.publisher_id,
          }"
          filter
        />
        <small
          v-if="
            submitted &&
            campaign.campaign_type === 'publisher' &&
            !campaign.publisher_id
          "
          class="p-error"
        >
          Publisher ID is required
        </small>
      </div>
    </div>

    <div class="col-12 md:col-3 flex flex-column gap-1">
      <label for="status">Status</label>
      <Dropdown
        id="status"
        :modelValue="campaign.status"
        @update:modelValue="
          $emit('update:campaign', { ...campaign, status: $event })
        "
        :options="statusOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Choose Status"
      />
    </div>

    <div class="col-12 md:col-6">
      <div class="grid">
        <div class="col-3">
          <label for="growth_percentage">Growth (%)</label>
          <InputNumber
            id="growth_percentage"
            :modelValue="campaign.growth_percentage"
            @update:modelValue="
              $emit('update:campaign', {
                ...campaign,
                growth_percentage: $event,
              })
            "
            :min="0"
            :max="100"
            suffix="%"
            class="w-full"
            :inputStyle="{ width: '100%' }"
          />
        </div>
      </div>
    </div>

    <!-- CTR - Separate row for bulk mode with policy options -->
    <template v-if="isBulkMode">
      <div class="col-12">
        <div class="grid">
          <div class="col-12 md:col-3">
            <label for="ctr_policy_type">CTR Mode</label>
            <Dropdown
              id="ctr_policy_type"
              :modelValue="localCtrPolicyType"
              @update:modelValue="handleCtrPolicyTypeChange"
              :options="ctrPolicyOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select Mode"
              class="w-full"
            />
          </div>

          <!-- Same for all shows mode -->
          <div v-if="localCtrPolicyType === 'same'" class="col-12 md:col-6">
            <label for="ctr">CTR</label>
            <InputText
              id="ctr"
              :modelValue="campaign.ctr"
              @update:modelValue="
                $emit('update:campaign', { ...campaign, ctr: $event })
              "
              :class="{ 'p-invalid': submitted && !campaign.ctr }"
              :disabled="campaign.status === 'Mellow mode'"
            />
            <small
              v-if="
                submitted && !campaign.ctr && campaign.status !== 'Mellow mode'
              "
              class="p-error"
            >
              CTR is required
            </small>
          </div>

          <!-- Random range mode -->
          <div v-if="localCtrPolicyType === 'random'" class="col-12 md:col-4">
            <label for="ctr_min">Min CTR</label>
            <InputNumber
              id="ctr_min"
              :modelValue="localCtrMin"
              @update:modelValue="handleCtrMinChange"
              :min="0"
              :minFractionDigits="2"
              :maxFractionDigits="4"
              class="w-full"
              :inputStyle="{ width: '100%' }"
              :class="{
                'p-invalid': submitted && localCtrMin === null,
              }"
            />
            <small v-if="submitted && localCtrMin === null" class="p-error">
              Min CTR is required
            </small>
          </div>

          <div v-if="localCtrPolicyType === 'random'" class="col-12 md:col-4">
            <label for="ctr_max">Max CTR</label>
            <InputNumber
              id="ctr_max"
              :modelValue="localCtrMax"
              @update:modelValue="handleCtrMaxChange"
              :min="0"
              :minFractionDigits="2"
              :maxFractionDigits="4"
              class="w-full"
              :inputStyle="{ width: '100%' }"
              :class="{
                'p-invalid': submitted && localCtrMax === null,
              }"
            />
            <small v-if="submitted && localCtrMax === null" class="p-error">
              Max CTR is required
            </small>
          </div>
        </div>
      </div>
    </template>

    <!-- Regular CTR for non-bulk mode (single mode) -->
    <template v-else>
      <div class="col-12 md:col-6">
        <label for="ctr">CTR</label>
        <InputNumber
          id="ctr"
          :modelValue="campaign.ctr"
          @update:modelValue="
            $emit('update:campaign', { ...campaign, ctr: $event })
          "
          :min="0"
          :minFractionDigits="2"
          :maxFractionDigits="4"
          class="w-full"
          :inputStyle="{ width: '100%' }"
          :class="{ 'p-invalid': submitted && !campaign.ctr }"
          :disabled="campaign.status === 'Mellow mode'"
        />
        <small
          v-if="submitted && !campaign.ctr && campaign.status !== 'Mellow mode'"
          class="p-error"
        >
          CTR is required
        </small>
      </div>
    </template>

    <!-- Promotions Per Interval - Only show if configurable OR if not configurable and type is "same" -->
    <template v-if="isBulkMode">
      <div class="col-12">
        <div class="grid">
          <div class="col-12 md:col-3">
            <label for="interval_policy_type"
              >Promotions Per Interval Mode</label
            >
            <Dropdown
              id="interval_policy_type"
              :modelValue="localIntervalPolicyType"
              @update:modelValue="handleIntervalPolicyTypeChange"
              :options="intervalPolicyOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select Mode"
              class="w-full"
            />
          </div>

          <!-- Same for all shows mode -->
          <div
            v-if="localIntervalPolicyType === 'same'"
            class="col-12 md:col-6"
          >
            <label for="promotions_per_interval">Promotions Per Interval</label>
            <div class="p-inputgroup">
              <InputText
                id="promotions_per_interval"
                :modelValue="campaign.promotions_per_interval"
                @update:modelValue="
                  $emit('update:campaign', {
                    ...campaign,
                    promotions_per_interval: $event,
                  })
                "
                :class="{
                  'p-invalid': submitted && !campaign.promotions_per_interval,
                }"
                :disabled="campaign.status === 'Mellow mode'"
              />
              <Button
                v-if="showCalculateButton"
                type="button"
                icon="pi pi-calculator"
                @click="$emit('calculate-promotions')"
                :disabled="campaign.status === 'Mellow mode'"
              />
            </div>
            <small
              v-if="
                submitted &&
                !campaign.promotions_per_interval &&
                campaign.status !== 'Mellow mode'
              "
              class="p-error"
            >
              Promotions Per Interval is required
            </small>
          </div>

          <!-- Random range mode -->
          <div
            v-if="localIntervalPolicyType === 'random'"
            class="col-12 md:col-4"
          >
            <label for="interval_min">Min Value</label>
            <InputNumber
              id="interval_min"
              :modelValue="localIntervalMin"
              @update:modelValue="handleIntervalMinChange"
              :min="0.1"
              :step="0.1"
              :minFractionDigits="1"
              :maxFractionDigits="1"
              class="w-full"
              :inputStyle="{ width: '100%' }"
              :class="{
                'p-invalid': submitted && localIntervalMin === null,
              }"
            />
            <small
              v-if="submitted && localIntervalMin === null"
              class="p-error"
            >
              Min value is required
            </small>
          </div>

          <div
            v-if="localIntervalPolicyType === 'random'"
            class="col-12 md:col-4"
          >
            <label for="interval_max">Max Value</label>
            <InputNumber
              id="interval_max"
              :modelValue="localIntervalMax"
              @update:modelValue="handleIntervalMaxChange"
              :min="0.1"
              :step="0.1"
              :minFractionDigits="1"
              :maxFractionDigits="1"
              class="w-full"
              :inputStyle="{ width: '100%' }"
              :class="{
                'p-invalid': submitted && localIntervalMax === null,
              }"
            />
            <small
              v-if="submitted && localIntervalMax === null"
              class="p-error"
            >
              Max value is required
            </small>
          </div>
        </div>
      </div>
    </template>

    <!-- Regular Promotions Per Interval for non-configurable mode -->
    <template v-else>
      <div class="col-12 md:col-6">
        <label for="promotions_per_interval">Promotions Per Interval</label>
        <div class="p-inputgroup">
          <InputNumber
            id="promotions_per_interval"
            :modelValue="campaign.promotions_per_interval"
            @update:modelValue="
              $emit('update:campaign', {
                ...campaign,
                promotions_per_interval: $event,
              })
            "
            :min="0"
            :minFractionDigits="1"
            :maxFractionDigits="2"
            class="w-full"
            :inputStyle="{ width: '100%' }"
            :class="{
              'p-invalid': submitted && !campaign.promotions_per_interval,
            }"
            :disabled="campaign.status === 'Mellow mode'"
          />
          <Button
            v-if="showCalculateButton"
            type="button"
            icon="pi pi-calculator"
            @click="$emit('calculate-promotions')"
            :disabled="campaign.status === 'Mellow mode'"
          />
        </div>
        <small
          v-if="
            submitted &&
            !campaign.promotions_per_interval &&
            campaign.status !== 'Mellow mode'
          "
          class="p-error"
        >
          Promotions Per Interval is required
        </small>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import Calendar from "primevue/calendar";
import MultiSelect from "primevue/multiselect";

// eslint-disable-next-line no-undef
const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
  countries: {
    type: Array,
    required: true,
  },
  sellers: {
    type: Array,
    required: true,
  },
  shows: {
    type: Array,
    default: () => [],
  },
  hasShowDropdown: {
    type: Boolean,
    default: false,
  },
  showCalculateButton: {
    type: Boolean,
    default: true,
  },
  submitted: {
    type: Boolean,
    default: false,
  },
  isBulkMode: {
    type: Boolean,
    default: false,
  },
  showDownloadsBasedOption: {
    type: Boolean,
    default: false,
  },
});

// eslint-disable-next-line no-undef
const emit = defineEmits(["update:campaign", "calculate-promotions"]);

const campaignTypeOptions = [
  { label: "promotion", value: "promotion" },
  { label: "publisher", value: "publisher" },
  { label: "spotify", value: "spotify" },
];

const statusOptions = computed(() => {
  const baseOptions = [
    { label: "active", value: "active" },
    { label: "pending", value: "pending" },
    { label: "in-active", value: "in-active" },
  ];

  if (!props.isBulkMode) {
    baseOptions.push({ label: "Mellow mode", value: "Mellow mode" });
  }

  return baseOptions;
});

const intervalPolicyOptions = computed(() => {
  const options = [
    { label: "Same for all shows", value: "same" },
    { label: "Random (range)", value: "random" },
  ];

  if (props.showDownloadsBasedOption) {
    options.push({ label: "Based on Downloads", value: "avgDownloadsBased" });
  }

  return options;
});

const ctrPolicyOptions = [
  { label: "Same for all shows", value: "same" },
  { label: "Random (range)", value: "random" },
];

// Local state for interval policy
const localIntervalPolicyType = ref("same");
const localIntervalMin = ref(null);
const localIntervalMax = ref(null);

// Local state for CTR policy
const localCtrPolicyType = ref("same");
const localCtrMin = ref(null);
const localCtrMax = ref(null);

// Initialize from campaign data if exists
watch(
  () => props.campaign.intervalPolicy,
  (newPolicy) => {
    if (newPolicy && props.isBulkMode) {
      localIntervalPolicyType.value = newPolicy.type || "same";
      if (newPolicy.type === "random") {
        localIntervalMin.value = newPolicy.min ?? null;
        localIntervalMax.value = newPolicy.max ?? null;
      }
    }
  },
  { immediate: true }
);

watch(
  () => props.campaign.ctrPolicy,
  (newPolicy) => {
    if (newPolicy && props.isBulkMode) {
      localCtrPolicyType.value = newPolicy.type || "same";
      if (newPolicy.type === "random") {
        localCtrMin.value = newPolicy.min ?? null;
        localCtrMax.value = newPolicy.max ?? null;
      }
    }
  },
  { immediate: true }
);

const handleIntervalPolicyTypeChange = (newType) => {
  localIntervalPolicyType.value = newType;

  let updatedCampaign = { ...props.campaign };

  if (newType === "same") {
    // For "same", we keep intervalPolicy but remove policy-specific values
    updatedCampaign.intervalPolicy = { type: "same" };
  } else if (newType === "random") {
    const min = localIntervalMin.value ?? 0.5;
    const max = localIntervalMax.value ?? 1;
    localIntervalMin.value = min;
    localIntervalMax.value = max;
    updatedCampaign.intervalPolicy = { type: "random", min, max };
  } else if (newType === "avgDownloadsBased") {
    updatedCampaign.intervalPolicy = { type: "avgDownloadsBased" };
  }

  emit("update:campaign", updatedCampaign);
};

const handleIntervalMinChange = (value) => {
  localIntervalMin.value = value;
  emit("update:campaign", {
    ...props.campaign,
    intervalPolicy: {
      type: "random",
      min: value,
      max: localIntervalMax.value,
    },
  });
};

const handleIntervalMaxChange = (value) => {
  localIntervalMax.value = value;
  emit("update:campaign", {
    ...props.campaign,
    intervalPolicy: {
      type: "random",
      min: localIntervalMin.value,
      max: value,
    },
  });
};

const handleCtrPolicyTypeChange = (newType) => {
  localCtrPolicyType.value = newType;

  let updatedCampaign = { ...props.campaign };

  if (newType === "same") {
    updatedCampaign.ctrPolicy = { type: "same" };
  } else if (newType === "random") {
    const min = localCtrMin.value ?? 0.1;
    const max = localCtrMax.value ?? 0.9;
    localCtrMin.value = min;
    localCtrMax.value = max;
    updatedCampaign.ctrPolicy = { type: "random", min, max };
  }

  emit("update:campaign", updatedCampaign);
};

const handleCtrMinChange = (value) => {
  localCtrMin.value = value;
  emit("update:campaign", {
    ...props.campaign,
    ctrPolicy: {
      type: "random",
      min: value,
      max: localCtrMax.value,
    },
  });
};

const handleCtrMaxChange = (value) => {
  localCtrMax.value = value;
  emit("update:campaign", {
    ...props.campaign,
    ctrPolicy: {
      type: "random",
      min: localCtrMin.value,
      max: value,
    },
  });
};
</script>

<style scoped>
.p-inputnumber,
.p-inputtext,
.p-inputgroup {
  width: 100%;
}

.p-inputgroup {
  display: flex;
}

.p-inputgroup .p-inputtext {
  flex: 1;
}

.p-inputgroup .p-button {
  flex: 0 0 auto;
}

label {
  display: block;
  margin-bottom: 0.5rem;
}

input {
  max-width: 100%;
}
</style>
